import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { api } from './api'
import { en } from '../locales/en'
import { pl } from '../locales/pl'

/**
 * Dependency-free i18n for the SPA (SPEC §E). Chosen over react-i18next: the app is
 * a small SPA with simple {var} interpolation, and a custom provider gives exact
 * control over the two product requirements — **EN default for anonymous visitors**
 * and **persistence (localStorage + PATCH User.locale)** — with zero build risk.
 *
 * Resolution order for the active locale:
 *   1. explicit user choice this session / a prior localStorage pick
 *   2. the logged-in User.locale (server is source of truth once authenticated)
 *   3. EN by default (anonymous entry → English, SPEC assumption 8)
 */
export type Locale = 'en' | 'pl'
const LOCALES: Record<Locale, Record<string, unknown>> = { en, pl }
const STORAGE_KEY = 'edimi.locale'

function isLocale(v: unknown): v is Locale {
  return v === 'en' || v === 'pl'
}

/** Look up a dotted key (e.g. "nav.billing") in a nested resource object. */
function lookup(table: Record<string, unknown>, key: string): string | undefined {
  let node: unknown = table
  for (const part of key.split('.')) {
    if (node && typeof node === 'object' && part in (node as Record<string, unknown>)) {
      node = (node as Record<string, unknown>)[part]
    } else {
      return undefined
    }
  }
  return typeof node === 'string' ? node : undefined
}

export type TFunc = (key: string, vars?: Record<string, string | number>) => string

interface I18nValue {
  locale: Locale
  /**
   * `onResult` jest opcjonalny i DOPISANY (sesja 38, artboardy 07h i 09g): karta
   * Konto w Ustawieniach potwierdza zapis języka toastem, a nieudany zapis pokazuje
   * w wierszu przy przełączniku, z kodem dla pomocy. Bez niego zapis pozostaje
   * best-effort jak dotąd — gość nie ma czego zapisywać, a nawigacja nie pyta.
   */
  setLocale: (next: Locale, onResult?: (ok: boolean, e?: unknown) => void) => void
  setRouteLocale: (next: Locale) => void
  t: TFunc
}

// Module-level translator, shared by the provider and the default context value so
// useI18n() is safe to call outside a provider (e.g. an isolated component unit test),
// resolving to English rather than crashing.
function translate(locale: Locale, key: string, vars?: Record<string, string | number>): string {
  const raw = lookup(LOCALES[locale], key) ?? lookup(LOCALES.en, key) ?? key
  if (!vars) return raw
  return raw.replace(/\{(\w+)\}/g, (_, name) => String(vars[name] ?? `{${name}}`))
}

/**
 * Translate a value that ARRIVES FROM THE API (a status, a mode) rather than one we chose.
 *
 * `translate()` above ends in `?? key`, which is right for a hardcoded key -- a typo shows
 * up as a visible dotted path instead of an empty screen. It is WRONG for `t(`ns.${value}`)`,
 * because the value is not ours to guarantee: an older row, a new enum member the catalogue
 * has not caught up with, or a fixture that invented one, and the artist reads
 * `appCampaigns.status.active` on the page. That is strictly worse than the untranslated
 * word, which at least means something.
 *
 * So: translate when the catalogue knows the value, otherwise show a NEUTRAL SENTENCE from
 * the catalogue (`common.unspecified`) — never the raw value (G7-account-token2-34). A raw
 * `schedule` or `brand_new_status` mid-sentence is a foreign word to a Polish reader and a
 * machine token to every reader; the neutral word says "the server knows more than this
 * screen does" without pretending to be a label.
 */
/**
 * A translator bound to ONE locale, for code that has no React context — tests that exercise
 * a real mapper, and anything that must answer "what does a Polish reader actually get?".
 *
 * Deliberately a thin wrapper over the same `translate` the provider uses (:51), not a second
 * lookup: a test that reimplemented the dotted-key walk would prove things about its own copy.
 */
export function translateFor(locale: Locale): TFunc {
  return (key, vars) => translate(locale, key, vars)
}

export function tEnum(t: TFunc, prefix: string, value: string): string {
  const key = `${prefix}.${value}`
  const label = t(key)
  return label === key ? t('common.unspecified') : label
}

// Default context: used only when NO <I18nProvider> is mounted — app code is always
// under the provider (main.tsx), so in production this is inert. Outside a provider
// (component unit tests, or the instant before mount) we still honour a persisted
// localStorage pick via initialLocale() so tests render in the saved locale rather
// than always English. `initialLocale` is a hoisted function declaration (defined below).
const I18nCtx = createContext<I18nValue>({
  get locale(): Locale { return initialLocale() },
  setLocale: () => {},
  setRouteLocale: () => {},
  t: (key, vars) => translate(initialLocale(), key, vars),
})

export function useI18n(): I18nValue {
  return useContext(I18nCtx)
}

/** Convenience hook returning just the translator. */
export function useT(): TFunc {
  return useI18n().t
}

function initialLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (isLocale(stored)) return stored
  } catch {
    /* SSR / blocked storage — fall through to browser detection */
  }
  // No explicit pick yet → auto-detect from the browser (Domi 2026-06-26, supersedes
  // the old hardcoded 'en' / "SPEC assumption 8"): a Polish-language browser gets PL
  // (the primary market) so a direct /login or /register visit isn't English; every
  // other browser falls back to EN.
  try {
    if ((navigator.language || '').toLowerCase().startsWith('pl')) return 'pl'
  } catch {
    /* no navigator (SSR) */
  }
  return 'en'
}

/**
 * `userLocale` is threaded in (rather than calling useAuth here) so I18nProvider can
 * sit OUTSIDE AuthProvider if ever needed and to keep the dependency one-way. main.tsx
 * mounts it inside AuthProvider and passes user.locale through <I18nProvider>.
 */
export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale)
  // Whether the user has made an explicit pick this session — once they have, a later
  // server User.locale must NOT silently override their choice.
  const [explicit, setExplicit] = useState<boolean>(() => {
    try { return isLocale(localStorage.getItem(STORAGE_KEY)) } catch { return false }
  })

  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])

  const setLocale = useCallback((next: Locale, onResult?: (ok: boolean, e?: unknown) => void) => {
    setLocaleState(next)
    setExplicit(true)
    try { localStorage.setItem(STORAGE_KEY, next) } catch { /* ignore */ }
    // Persist to the profile when authenticated (best-effort; anon stays local-only).
    // The UI has ALREADY switched: the language a reader chose is theirs whether or
    // not the profile write lands, so a failure is reported where it can be acted on
    // and never by putting the interface back into the language they just left.
    api('/auth/me', { method: 'PATCH', body: JSON.stringify({ locale: next }) })
      .then(() => { onResult?.(true) })
      .catch((e) => { onResult?.(false, e) })
  }, [])

  const setRouteLocale = useCallback((next: Locale) => {
    setLocaleState(next)
  }, [])

  /** Called by main.tsx when the logged-in user's locale becomes known. */
  const adoptUserLocale = useCallback((userLocale?: string) => {
    if (!explicit && isLocale(userLocale)) setLocaleState(userLocale)
  }, [explicit])

  const t = useCallback<TFunc>((key, vars) => translate(locale, key, vars), [locale])

  const value = useMemo<I18nValue & { adoptUserLocale: typeof adoptUserLocale }>(
    () => ({ locale, setLocale, setRouteLocale, t, adoptUserLocale }),
    [locale, setLocale, setRouteLocale, t, adoptUserLocale],
  )

  return <I18nCtx.Provider value={value}>{children}</I18nCtx.Provider>
}

/** Bridge: adopt the authenticated User.locale (unless the user picked explicitly). */
export function useAdoptUserLocale(userLocale?: string) {
  const ctx = useContext(I18nCtx) as I18nValue & { adoptUserLocale?: (l?: string) => void }
  // Depend on the adopter, not on the whole context value. The value is rebuilt on every
  // locale change, so an effect keyed on it re-adopted the profile locale after EVERY change
  // — including the one `LocaleRoute` makes for a public `/en/...` address — and the two
  // effects took turns forever (measured 25.09 on the demo: 23 "Maximum update depth
  // exceeded" warnings in 8 s on `/en`, a busy loop for every English page of a PL profile).
  // `adoptUserLocale` only changes identity when the explicit pick changes, so the profile
  // locale is adopted once per known value and the address keeps the last word on public pages.
  const adopt = ctx?.adoptUserLocale
  useEffect(() => {
    adopt?.(userLocale)
  }, [adopt, userLocale])
}
